import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Alert } from '../components/Alert';
import { AuthLayout } from '../components/AuthLayout';
import { Button } from '../components/Button';
import { Field } from '../components/Field';
import { api } from '../lib/api';
import { messageForError } from '../lib/errors';
import { parseForm, resetPasswordFormSchema, type FieldErrors } from '../lib/forms';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [errors, setErrors] = useState<FieldErrors<'password' | 'confirm' | 'token'>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const r = parseForm(resetPasswordFormSchema, e.currentTarget);
    if (r.errors) return setErrors(r.errors as FieldErrors<'password' | 'confirm' | 'token'>);
    setErrors({});
    setError(null);
    setBusy(true);
    try {
      await api.auth.resetPassword({ token: r.data.token, password: r.data.password });
      void navigate('/login?reset=1', { replace: true });
    } catch (err) {
      setError(messageForError(err));
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout title="Choose a new password">
        <Alert tone="error">This link is missing its token. Request a new reset email.</Alert>
        <Link
          to="/forgot-password"
          className="text-center font-medium text-brand underline-offset-2 hover:underline"
        >
          Request a new link
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Choose a new password">
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {error ? <Alert tone="error">{error}</Alert> : null}
        {errors.token ? <Alert tone="error">{errors.token}</Alert> : null}
        <input type="hidden" name="token" value={token} />
        <Field
          id="password"
          name="password"
          label="New password"
          type="password"
          autoComplete="new-password"
          required
          minLength={10}
          error={errors.password}
        />
        <Field
          id="confirm"
          name="confirm"
          label="Confirm new password"
          type="password"
          autoComplete="new-password"
          required
          error={errors.confirm}
        />
        <Button type="submit" disabled={busy}>
          {busy ? 'Saving…' : 'Set new password'}
        </Button>
      </form>
    </AuthLayout>
  );
}
